import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, BrainCircuit, Sparkles, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReportBody({ text }) {
    return (
        <div className="text-sm leading-relaxed space-y-1">
            {text.split('\n').map((line, i) => {
                const t = line.trim();
                if (!t) return <div key={i} className="h-2" />;
                if (t.startsWith('##')) return <div key={i} className="font-heading text-base text-primary mt-3">{t.replace(/^#+\s*/, '')}</div>;
                if (t.startsWith('- ') || t.startsWith('• ')) return <div key={i} className="pl-4 relative"><span className="absolute left-0 text-primary">•</span>{t.slice(2)}</div>;
                return <p key={i}>{t.replace(/\*\*/g, '')}</p>;
            })}
        </div>
    );
}

export default function AdrnsIntelligence({ dossierId }) {
    const { session } = useAuth();
    const [items, setItems] = useState(null);
    const [generating, setGenerating] = useState(false);
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    useEffect(() => {
        if (!session) return;
        axios.get(`${API}/adrns/dossiers/${dossierId}/intelligence`, hdrs())
            .then(r => setItems(r.data.items)).catch(() => setItems([]));
        // eslint-disable-next-line
    }, [session, dossierId]);

    const generate = async () => {
        setGenerating(true);
        try {
            const r = await axios.post(`${API}/adrns/dossiers/${dossierId}/intelligence`, {}, hdrs());
            setItems(prev => [r.data.item, ...(prev || [])].slice(0, 5));
            toast.success('Analyse 360° générée');
        } catch (e) { toast.error(e.response?.data?.detail || 'Intelligence 360° indisponible'); }
        setGenerating(false);
    };

    const exportPdf = async () => {
        try {
            const r = await axios.get(`${API}/adrns/dossiers/${dossierId}/intelligence.pdf`, { ...hdrs(), responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = 'intelligence360.pdf'; a.click();
            URL.revokeObjectURL(url);
            toast.success('Analyse 360° exportée en PDF');
        } catch { toast.error('Export impossible'); }
    };

    const latest = items?.[0];

    return (
        <div className="card-soft p-6 border-primary/20" data-testid="adrns-intelligence">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <h2 className="font-heading text-xl flex items-center gap-2">
                    <BrainCircuit size={18} className="text-primary" /> Intelligence African Valley 360°
                </h2>
                <div className="flex items-center gap-2">
                    {latest && (
                        <Button size="sm" variant="outline" onClick={exportPdf} data-testid="adrns-intelligence-pdf">
                            <FileDown size={13} className="mr-1.5 text-primary" /> PDF
                        </Button>
                    )}
                    <Button size="sm" onClick={generate} disabled={generating} data-testid="adrns-intelligence-generate">
                        {generating ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Analyse en cours…</> : <><Sparkles size={13} className="mr-1.5" /> {latest ? 'Régénérer l\'analyse' : 'Générer l\'analyse 360°'}</>}
                    </Button>
                </div>
            </div>
            {items === null ? (
                <div className="flex justify-center py-6"><Loader2 className="animate-spin" size={18} /></div>
            ) : !latest ? (
                <p className="text-sm text-foreground/60">Générez une analyse complète du dossier par l'IA : synthèse, points forts, vigilances, recommandations et prochaines étapes.</p>
            ) : (
                <div data-testid="adrns-intelligence-report">
                    <div className="text-xs text-muted-foreground mb-3">Générée par {latest.author_name} · {new Date(latest.at).toLocaleString('fr-FR')}</div>
                    <ReportBody text={latest.report} />
                </div>
            )}
        </div>
    );
}
